// components/journey-surface/journey-layout.js
// ═══════════════════════════════════════════════
// Pure layout functions for the journey map radial mindmap.
// No React, no side effects — pure data transforms.

import { NODE_TYPES } from '../../lib/workspace-config';

// ── Layout constants ───────────────────────────────────────
export const NODE_CIRCLE_RADIUS = 18;
export const NODE_RECT_WIDTH = 44;
export const NODE_RECT_HEIGHT = 30;
export const VERTICAL_SPACING = 64;
export const HORIZONTAL_SPACING = 70;
export const TOP_PADDING = 36;
export const RAIL_WIDTH = 40;

// ── Label generator ────────────────────────────────────────
// Assigns each node a short label like M1, Q2, D1, I1, F3, R based on
// its type and order of appearance. Mirrors the wireframe at
// shiftirl.ai/space-interactive where nodes are tagged W1/Calc/etc and
// the legend below maps each tag to its description.
const TYPE_PREFIX = {
  root: 'R',
  milestone: 'M',
  question: 'Q',
  decision: 'D',
  insight: 'I',
  file: 'F',
};

export function buildLabels(nodes) {
  // Stable order: created_at ascending, then id as tiebreaker
  const ordered = [...(nodes || [])].sort((a, b) => {
    const at = a.created_at ? new Date(a.created_at).getTime() : 0;
    const bt = b.created_at ? new Date(b.created_at).getTime() : 0;
    if (at !== bt) return at - bt;
    return (a.id || '').localeCompare(b.id || '');
  });
  const counters = {};
  const labels = {};
  ordered.forEach(n => {
    const prefix = TYPE_PREFIX[n.type] || '?';
    if (n.type === 'root') {
      labels[n.id] = 'R';
      return;
    }
    counters[prefix] = (counters[prefix] || 0) + 1;
    labels[n.id] = `${prefix}${counters[prefix]}`;
  });
  return labels;
}

// ── Logical tree builder ───────────────────────────────────
// Computes a flat tree where the root is the only parent and ALL
// other nodes are direct children (depth = 1). The visual hierarchy
// comes from the radial layout grouping same-type children into
// angular wedges.
//
// Why flat instead of chained: chains of same-type nodes degenerate
// into straight lines in radial layout because each single-child
// parent passes its angle to its child. A flat tree with type
// grouping produces a real starburst that visibly clusters by type.
//
// Old workspaces with chronological parent_ids in the DB ALSO render
// correctly because we ignore the stored parent_id and rebuild from
// type + creation order at render time. New workspaces use the same
// logic via createMomentNode → pickSmartParent.
export function buildLogicalTree(nodes) {
  if (!nodes || nodes.length === 0) return { root: null, byId: {} };

  // Sort by created_at ascending so siblings appear in time order
  const ordered = [...nodes].sort((a, b) => {
    const at = a.created_at ? new Date(a.created_at).getTime() : 0;
    const bt = b.created_at ? new Date(b.created_at).getTime() : 0;
    if (at !== bt) return at - bt;
    return (a.id || '').localeCompare(b.id || '');
  });

  const byId = {};
  ordered.forEach(n => { byId[n.id] = { ...n, children: [] }; });

  let root = ordered.find(n => n.type === NODE_TYPES.ROOT);
  if (!root) root = ordered[0];
  const rootCopy = byId[root.id];

  // Flat: every non-root node is a direct child of root
  ordered.forEach(n => {
    if (n.id === rootCopy.id) return;
    byId[n.id].parent_id = rootCopy.id;
    rootCopy.children.push(byId[n.id]);
  });

  return { root: rootCopy, byId };
}

// ── Radial mindmap layout (type-grouped flat) ──────────────
// Root at center. All non-root nodes are children of root and get
// placed in TYPE WEDGES around the circle. Each type owns an angular
// slice; nodes of that type spread evenly within their slice.
//
// For 4 types with 10 nodes (e.g. 6 milestones + 2 questions + 2
// decisions), each type owns 90° and the milestone wedge has 6 nodes
// at 12-15° increments. Visibly clustered, no chains, no straight
// lines.
//
// Files cluster as a single wedge regardless of file_source, so the
// canvas tile grid is the place that distinguishes uploaded vs
// generated, not the journey map.
const RADIAL_RADIUS = 100;     // distance from center to each node
const RADIAL_SIZE = 320;       // square viewBox — fits ~8-12 nodes comfortably

export function computeRadialLayout(nodes, _viewWidth) {
  if (!nodes || nodes.length === 0) return { positioned: [], viewSize: RADIAL_SIZE };

  const { root, byId } = buildLogicalTree(nodes);
  if (!root) return { positioned: [], viewSize: RADIAL_SIZE };

  const cx = RADIAL_SIZE / 2;
  const cy = RADIAL_SIZE / 2;

  // Place root at center
  root.x = cx;
  root.y = cy;
  root.depth = 0;

  // Group children of root by type so each type gets its own wedge
  // The order matters: types with more nodes get adjacent positions
  // to avoid wasted angular space.
  const TYPE_ORDER = [
    NODE_TYPES.DECISION,
    NODE_TYPES.QUESTION,
    NODE_TYPES.INSIGHT,
    NODE_TYPES.MILESTONE,
    NODE_TYPES.FILE,
  ];
  const grouped = {};
  TYPE_ORDER.forEach(t => { grouped[t] = []; });
  root.children.forEach(c => {
    if (grouped[c.type] !== undefined) grouped[c.type].push(c);
    else {
      // Unknown type — bucket under decision as a fallback
      grouped[NODE_TYPES.DECISION].push(c);
    }
  });

  // Only types with at least one node get a wedge
  const activeTypes = TYPE_ORDER.filter(t => grouped[t].length > 0);
  if (activeTypes.length === 0) {
    return { positioned: [root], viewSize: RADIAL_SIZE };
  }

  // Total angular budget split evenly across active types
  const wedgeSize = (Math.PI * 2) / activeTypes.length;

  activeTypes.forEach((type, typeIdx) => {
    const typeNodes = grouped[type];
    // Center this type's wedge at the angle (typeIdx * wedgeSize) - PI/2
    // so the first wedge starts at the top of the circle
    const wedgeCenter = (typeIdx * wedgeSize) - Math.PI / 2 + wedgeSize / 2;

    // Spread nodes within the wedge. Use ~80% of the wedge so adjacent
    // wedges don't touch.
    const usableSpread = wedgeSize * 0.8;
    const count = typeNodes.length;

    typeNodes.forEach((node, i) => {
      let angle;
      if (count === 1) {
        angle = wedgeCenter;
      } else {
        const t = i / (count - 1); // 0 to 1
        angle = wedgeCenter - usableSpread / 2 + t * usableSpread;
      }
      node.x = cx + Math.cos(angle) * RADIAL_RADIUS;
      node.y = cy + Math.sin(angle) * RADIAL_RADIUS;
      node.depth = 1;
      node.angle = angle;
    });
  });

  const positioned = Object.values(byId);
  return { positioned, viewSize: RADIAL_SIZE };
}
